import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

interface AccountMenuProps {
  name: string;
  email?: string;
  onLogout: () => void;
}

/** Name + arrow in the company/driver headers; opens the account details and "Log out". */
export default function AccountMenu({ name, email, onLogout }: AccountMenuProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t.header.accountMenu}
        className="flex h-9 shrink-0 cursor-pointer items-center gap-1 rounded border-0 bg-transparent px-1 text-[13px] sm:gap-2 font-medium text-ink transition-colors hover:bg-surface sm:px-2"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface text-xs font-semibold text-ink">
          {name.charAt(0).toUpperCase() || "?"}
        </span>
        <span className="hidden max-w-40 truncate md:block">{name}</span>
        <ChevronDown size={14} aria-hidden="true" className={cn("shrink-0 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div role="menu" className="absolute top-full right-0 z-50 mt-2 w-60 overflow-hidden rounded-card border border-line-strong bg-white p-1.5 shadow-lg">
          <div className="border-b border-line px-2.5 pt-1.5 pb-2.5">
            <div className="truncate text-sm font-medium text-ink">{name}</div>
            {email && <div className="truncate text-xs text-muted">{email}</div>}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="mt-1 block w-full cursor-pointer rounded border-0 bg-transparent px-2.5 py-2 text-left text-sm text-ink hover:bg-surface"
          >
            {t.common.logout}
          </button>
        </div>
      )}
    </div>
  );
}
